import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  AllApplicationListAPI,
  CreateCommentForApplication,
  DeleteApplication,
  DetailApplicationApi,
  ExportAllApplicationListAPI,
  ExportReferralApplicationListAPI,
  GetAllAppListDashboard,
  GetDashboardHeatMap,
  GetDashboardWorldWideData,
  IssuanceLetterApplication,
  ReferralApplicationListAPI,
  UpdateApplicationStatus,
} from "./applications.api";

export const applicationsKeys = {
  all: ["applications"],
  lists: () => [...applicationsKeys.all, "list"],
  list: (params) => [...applicationsKeys.lists(), params],
  referralLists: () => ["referral-applications", "list"],
  referralList: (params) => [...applicationsKeys.referralLists(), params],
  detail: (id) => [...applicationsKeys.all, "detail", id],
  dashboardLists: () => [...applicationsKeys.all, "dashboard-list"],
  dashboardList: (params) => [...applicationsKeys.dashboardLists(), params],
  heatMap: () => [...applicationsKeys.all, "dashboard-heat-map"],
  worldWide: () => [...applicationsKeys.all, "dashboard-world-wide"],
};

export const useAllApplicationsListQuery = (params, options = {}) =>
  useQuery({
    queryKey: applicationsKeys.list(params),
    queryFn: () => AllApplicationListAPI(params),
    ...options,
  });

export const useReferralApplicationsListQuery = (params, options = {}) =>
  useQuery({
    queryKey: applicationsKeys.referralList(params),
    queryFn: () => ReferralApplicationListAPI(params),
    ...options,
  });

export const useApplicationDetailQuery = (id, options = {}) =>
  useQuery({
    queryKey: applicationsKeys.detail(id),
    queryFn: () => DetailApplicationApi(id),
    enabled: Boolean(id),
    ...options,
  });

export const useDashboardApplicationListQuery = (params, options = {}) =>
  useQuery({
    queryKey: applicationsKeys.dashboardList(params),
    queryFn: () => GetAllAppListDashboard(params),
    ...options,
  });

export const useDashboardHeatMapQuery = () =>
  useQuery({
    queryKey: applicationsKeys.heatMap(),
    queryFn: GetDashboardHeatMap,
  });

export const useDashboardWorldWideQuery = () =>
  useQuery({
    queryKey: applicationsKeys.worldWide(),
    queryFn: GetDashboardWorldWideData,
  });

export const useExportAllApplicationsMutation = () =>
  useMutation({
    mutationFn: ExportAllApplicationListAPI,
  });

export const useExportReferralApplicationsMutation = () =>
  useMutation({
    mutationFn: ExportReferralApplicationListAPI,
  });

export const useDeleteApplicationMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: DeleteApplication,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: applicationsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: applicationsKeys.referralLists() });
      queryClient.invalidateQueries({ queryKey: applicationsKeys.dashboardLists() });
    },
  });
};

export const useCreateApplicationCommentMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: CreateCommentForApplication,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: applicationsKeys.detail(variables.application) });
      queryClient.invalidateQueries({ queryKey: applicationsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: applicationsKeys.referralLists() });
    },
  });
};

export const useUpdateApplicationStatusMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: UpdateApplicationStatus,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: applicationsKeys.detail(variables.application) });
      queryClient.invalidateQueries({ queryKey: applicationsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: applicationsKeys.referralLists() });
      queryClient.invalidateQueries({ queryKey: applicationsKeys.dashboardLists() });
    },
  });
};

export const useIssuanceLetterApplicationMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: IssuanceLetterApplication,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: applicationsKeys.detail(variables.application) });
      queryClient.invalidateQueries({ queryKey: applicationsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: applicationsKeys.referralLists() });
    },
  });
};
